import { Card, Button, Icon } from '@/components/common';
import { InlineLoader } from '@shared/ui/loading';
import { type ExportFormat } from '@/services/cv.service';
import { IconName } from '@/components/common/icons';
import { useState } from 'react';
import { TemplatePreview, type CVTemplate } from './TemplatePreview';

interface CVPreviewProps {
  cvId?: string;
  htmlContent: string;
  atsScore?: number;
  onExport?: (format: ExportFormat, template?: CVTemplate) => void;
  isExporting?: boolean;
}

export function CVPreview({
  cvId,
  htmlContent,
  atsScore,
  onExport,
  isExporting = false,
}: CVPreviewProps) {
  const [zoom, setZoom] = useState(100);
  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate>('professional');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const exportFormats: Array<{ format: ExportFormat; label: string; icon: IconName }> = [
    { format: 'pdf', label: 'PDF', icon: 'document' },
    { format: 'docx', label: 'DOCX', icon: 'file' },
    { format: 'html', label: 'HTML', icon: 'external-link' },
    { format: 'txt', label: 'TXT', icon: 'edit' },
  ];

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleZoomReset = () => {
    setZoom(100);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="p-3 sm:p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                className="p-2 rounded hover:bg-surface-hover"
                title="Zoom Out"
                disabled={zoom <= 50}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                  />
                </svg>
              </button>
              <span className="text-sm font-medium w-12 text-center">{zoom}%</span>
              <button
                onClick={handleZoomIn}
                className="p-2 rounded hover:bg-surface-hover"
                title="Zoom In"
                disabled={zoom >= 200}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                  />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                className="px-3 py-1 text-sm rounded hover:bg-surface-hover"
              >
                Reset
              </button>
            </div>

            {/* ATS Score */}
            {atsScore !== undefined && (
              <div className="flex items-center gap-2 px-3 py-1 bg-primary-bg rounded-lg">
                <span className="text-sm text-text-secondary">ATS Score:</span>
                <span
                  className={`text-sm font-bold ${
                    atsScore >= 80
                      ? 'text-green-600'
                      : atsScore >= 60
                        ? 'text-yellow-600'
                        : 'text-red-600'
                  }`}
                >
                  {atsScore}%
                </span>
              </div>
            )}
          </div>

          {/* Export Buttons */}
          {onExport && cvId && (
            <div className="flex items-center gap-2">
              {/* Template Selector — visual picker with 4 thumbnails */}
              <div className="relative mr-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                  className="flex items-center gap-2"
                >
                  <Icon name="document" size={16} />
                  Template: {selectedTemplate.charAt(0).toUpperCase() + selectedTemplate.slice(1)}
                  <Icon name="chevron-down" size={14} className={showTemplateSelector ? 'rotate-180' : ''} />
                </Button>
                {showTemplateSelector && (
                  <div className="absolute right-0 top-full mt-2 z-20 bg-surface rounded-lg shadow-xl border border-border p-4 w-[640px] max-w-[90vw]">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-sm font-semibold text-text-primary">Choose a template</div>
                        <div className="text-xs text-text-muted mt-0.5">
                          Click a template to select it. Then export to PDF/DOCX/HTML/TXT.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTemplateSelector(false)}
                        className="text-text-muted hover:text-text-primary p-1"
                        aria-label="Close template selector"
                      >
                        <Icon name="x" size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {(['professional', 'modern', 'twoColumn', 'harvard'] as CVTemplate[]).map((template) => (
                        <TemplatePreview
                          key={template}
                          template={template}
                          selected={selectedTemplate === template}
                          onSelect={() => {
                            setSelectedTemplate(template);
                            setShowTemplateSelector(false);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <span className="text-sm text-text-secondary mr-2">Export as:</span>
              {exportFormats.map(({ format, label, icon }) => (
                <Button
                  key={format}
                  size="sm"
                  variant="secondary"
                  onClick={() => onExport(format, selectedTemplate)}
                  disabled={isExporting}
                  className="flex items-center gap-2"
                >
                  <Icon name={icon} size={16} />
                  {label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Preview */}
      <Card className="p-8 bg-surface overflow-auto" style={{ maxHeight: '800px' }}>
        {isExporting && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="text-center">
              <InlineLoader size="lg" />
              <p className="mt-2 text-text-secondary">Exporting CV...</p>
            </div>
          </div>
        )}

        <div
          className="mx-auto bg-white shadow-lg"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '20mm',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease',
          }}
        >
          <div
            dangerouslySetInnerHTML={{ __html: htmlContent }}
            className="prose prose-sm max-w-none"
          />
        </div>
      </Card>
    </div>
  );
}
